import { NavLink, useNavigate } from "react-router-dom";
import {
  Fuel, Zap, Wrench, Receipt, FileCheck2, ChevronRight, ArrowLeft, Plus,
} from "lucide-react";
import { useEntitlements } from "@/context/EntitlementsContext";
import { useAuth } from "@/context/AuthContext";

/*
 * AddHub — dynamic + Log menu.
 * Options are filtered by:
 *   - user permissions
 *   - subscription flags (existing entitlement service)
 *
 * "Service / Repair Request" opens the Service page (workflow-driven
 * approvals, quotes and completion — see /app/frontend/src/pages/Service.jsx).
 * "Upload Service / Repair Bill" is the OCR-first camera flow which persists
 * as a service_bill record and is visible on the Timeline.
 */
const OPTIONS = [
  { id: "refuel",         to: "/new",             icon: Fuel,        title: "Add Refuel",                    hint: "Petrol / Diesel / CNG fill-up. Snap the receipt to auto-fill.",      perm: "add_fuel",       accent: "#FF5A1F" },
  { id: "charging",       to: "/new/charging",    icon: Zap,         title: "Add Charging",                  hint: "EV / PHEV charging session — home or public.",                        perm: "add_charging",   flag: "ev_charging", accent: "#22D3EE" },
  { id: "service-request",to: "/service",         icon: Wrench,      title: "Service / Repair Request",      hint: "Raise a request, get admin approval, and track it to completion.",    permAny: ["log_service", "log_repair"], accent: "#FBBF24" },
  { id: "bill",           to: "/log/upload-bill", icon: Receipt,     title: "Upload Service / Repair Bill",  hint: "Camera-first. AI OCR extracts vendor, items and total.",              perm: "upload_invoice", accent: "#F472B6" },
  { id: "document",       to: "/documents",       icon: FileCheck2,  title: "Upload Document",               hint: "RC, Insurance or PUC — kept safe with expiry reminders.",             perm: "view_documents", accent: "#34D399" },
];

export default function AddHub() {
  const nav = useNavigate();
  const { flag } = useEntitlements();
  const { user, hasPerm } = useAuth();

  const options = OPTIONS.filter((o) => {
    if (o.flag && !flag(o.flag)) return false;
    if (o.perm && !hasPerm(o.perm)) return false;
    if (o.permAny && !o.permAny.some((p) => hasPerm(p))) return false;
    return true;
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="add-hub-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 border border-[var(--border-line)] hover:bg-[var(--hover)] flex items-center justify-center transition-colors rounded-lg"
          data-testid="add-hub-back"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="ft-overline text-xs">QUICK CAPTURE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            What do you want to log{user?.name ? `, ${user.name.split(" ")[0]}` : ""}?
          </h1>
        </div>
      </div>

      {options.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--border-line)] p-6 text-sm text-[var(--text-muted)]" data-testid="add-hub-empty">
          Your account doesn't have permission to log any records yet. Ask your Account Admin to grant you access.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {options.map((o) => {
          const Icon = o.icon;
          return (
            <NavLink
              key={o.id}
              to={o.to}
              className="group flex items-center gap-4 border border-[var(--border-line)] bg-[var(--bg-surface)] p-4 sm:p-5 hover:bg-[var(--hover)] transition-colors rounded-xl"
              data-testid={`add-hub-${o.id}`}
            >
              <div className="w-14 h-14 flex items-center justify-center flex-shrink-0 rounded-lg" style={{ background: o.accent, color: "#000" }}>
                <Icon size={26} strokeWidth={2.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-black tracking-tight">{o.title}</div>
                <div className="text-sm text-[var(--text-muted)] mt-0.5">{o.hint}</div>
              </div>
              <ChevronRight size={20} className="text-[var(--text-muted)] group-hover:text-[#FF5A1F] transition-colors" />
            </NavLink>
          );
        })}
      </div>

      <div className="border border-dashed border-[var(--border-line)] p-4 text-xs text-[var(--text-muted)] flex items-center gap-2 rounded-lg">
        <Plus size={14} /> Only the actions you're authorised for are shown here.
      </div>
    </div>
  );
}
